import { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Tab, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { Typography } from '@mui/material';
import * as XLSX from 'xlsx';
import { styles, COLORS } from './styles';
import {
  DataTableProps,
  FALLBACK_VALUE,
  parseSection,
  ResultsViewerProps,
  SheetTabsProps,
  TableData,
} from './helpers';

const SheetTabs = (props: SheetTabsProps) => (
  <Box sx={styles.sheetTabs}>
    <Tabs value={props.selectedSheet} onChange={(_e, val) => props.onChange(val)}>
      {props.sheetNames.map((name) => (
        <Tab key={name} label={name} value={name} disableRipple sx={styles.tab} />
      ))}
    </Tabs>
  </Box>
);

const LegendAndNotes = () => (
  <>
    <Box sx={styles.legendAndNotes}>
      <Box sx={styles.legendItem}>
        <Box sx={styles.legendSquareNew} />
        <Typography variant="body2" sx={styles.legendText}>
          New row (created in the database)
        </Typography>
      </Box>
      <Box sx={styles.legendItem}>
        <Box sx={styles.legendSquareUpdate} />
        <Typography variant="body2" sx={styles.legendText}>
          Existing row (updated in the database)
        </Typography>
      </Box>
    </Box>
    <Box component="ul" sx={styles.notesList}>
      <li>
        Columns ending with &apos;_id&apos; show a reference ID. The column to the right shows the
        actual name/value used to match that ID (e.g., &apos;country_id&apos; has a
        &apos;country&apos; column showing the matched name).
      </li>
      <li>
        Columns with &apos;(new)&apos; in the header show the new value; columns with
        &apos;(existing)&apos; show the previous value.
      </li>
      <li>
        Source dupes positions: means the source file had duplicates for that position, so only one
        row was considered.
      </li>
      <li>
        Action taken: &apos;insert&apos; means it did not exist and was created in the database;
        &apos;update&apos; means it already existed and we updated it.
      </li>
      <li>
        The &apos;Blanks&apos; table indicates which rows from the source file were ignored and
        highlights which mandatory columns were empty, causing those rows to be excluded.
      </li>
    </Box>
  </>
);

const DataTable = ({ headers, rows, partnerIndex }: DataTableProps) => {
  const actionIdx = useMemo(
    () => headers.findIndex((h) => String(h).toLowerCase() === 'action taken'),
    [headers],
  );

  // build DataGrid columns
  const columns: GridColDef[] = useMemo(() => {
    return headers.map((h, idx) => ({
      field: `c${idx}`,
      headerName: String(h),
      flex: 1,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => {
        const value = params.value as string | number | undefined;
        const row = params.row as Record<string, string | number>;
        const actionVal = String(row[`c${actionIdx}`] ?? '').toLowerCase();
        let backgroundColor: string | undefined;
        if (actionVal === 'update' && partnerIndex[idx] !== undefined) {
          const partnerVal = row[`c${partnerIndex[idx]}`];
          if (String(value ?? '') !== String(partnerVal ?? '')) {
            backgroundColor = COLORS.rowUpdate;
          }
        }
        return (
          <Box
            sx={{
              ...styles.cellValue,
              ...(backgroundColor ? { backgroundColor } : {}),
            }}
            title={String(value ?? '')}
          >
            {String(value ?? '')}
          </Box>
        );
      },
    }));
  }, [headers, partnerIndex, actionIdx]);

  const rowsForRender = useMemo(() => {
    return rows.map((row, id) => {
      const obj: Record<string, string | number> = { id };
      row.forEach((cell, idx) => {
        obj[`c${idx}`] = cell;
      });
      return obj;
    });
  }, [rows]);

  return (
    <>
      <DataGrid
        rows={rowsForRender}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
        getRowClassName={(params) => {
          const actionVal = String(params.row[`c${actionIdx}`] ?? '').toLowerCase();
          return actionVal === 'insert' ? 'insert-row' : '';
        }}
        sx={styles.dataGrid}
      />
    </>
  );
};

export const ResultsViewer = ({ fileBuffer }: ResultsViewerProps) => {
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [tables, setTables] = useState<TableData[]>([]);

  useEffect(() => {
    if (!fileBuffer) return;

    const wb = XLSX.read(fileBuffer, { type: 'array' });
    const names = wb.SheetNames;
    setWorkbook(wb);
    setSheetNames(names);
    setSelectedSheet(names[0]!);
  }, [fileBuffer]);

  useEffect(() => {
    if (!workbook || !selectedSheet) return;

    const sheet: XLSX.WorkSheet = workbook.Sheets[selectedSheet]!;

    const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: FALLBACK_VALUE }) as (
      | string
      | number
    )[][];

    // detect a separator row whose first cell is exactly 'BLANKS' (case-insensitive)
    const blanksSepIdx = json.findIndex(
      (r) =>
        String(r?.[0] ?? '')
          .trim()
          .toUpperCase() === 'BLANKS',
    );

    if (blanksSepIdx !== -1) {
      const beforeBlanks = json.slice(0, blanksSepIdx); // up to (but not including) BLANKS
      const afterBlanks = json.slice(blanksSepIdx); // start right after BLANKS row

      const resultsTable = parseSection(beforeBlanks, 'Results');
      const blanksTable = parseSection(afterBlanks, 'Blanks', true);

      const nextTables: TableData[] = [];
      if (resultsTable) nextTables.push(resultsTable);
      if (blanksTable) nextTables.push(blanksTable);
      setTables(nextTables);
    } else {
      const t = parseSection(json, 'Results');
      setTables(t ? [t] : []);
    }
  }, [workbook, selectedSheet]);

  if (!tables.length) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.excelPreviewContainer}>
      <SheetTabs
        sheetNames={sheetNames}
        selectedSheet={selectedSheet}
        onChange={(name) => setSelectedSheet(name)}
      />
      <LegendAndNotes />
      {tables.map((t, idx) => (
        <Box key={idx} sx={styles.dataTableWrapper}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, opacity: 0.8 }}>
            {t.label || `Table ${idx + 1}`}
          </Typography>
          <DataTable headers={t.headers} rows={t.rows} partnerIndex={t.partnerIndex} />
        </Box>
      ))}
    </Box>
  );
};
